import React, { useState } from 'react';
import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import { IRoom, roomSchema } from '../Interfaces/IRoom';
import { useAlert } from '../core/AlertContext';
import { useCreateRoom, useUpdateRoom } from '../hooks/rooms';
import { fieldSx, primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

interface RoomEditProps {
    initialRoom: IRoom;
    onClose: () => void;
    showHeading?: boolean;
}

const RoomEdit: React.FC<RoomEditProps> = ({ initialRoom, onClose, showHeading = true }) => {
    const [room, setRoom] = useState<IRoom>(initialRoom);
    const isCreateMode = initialRoom.id < 0;
    const { createRoom, isCreatingRoom } = useCreateRoom();
    const { updateRoom, isUpdatingRoom } = useUpdateRoom();
    const { showAlert } = useAlert();
    const isSaving = isCreatingRoom || isUpdatingRoom;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setRoom((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const payload = roomSchema.pick({
                name: true,
                description: true,
            }).parse(room);

            if (isCreateMode) {
                await createRoom(payload);
                showAlert({ text: 'Zimmer erfolgreich angelegt.' });
            } else {
                await updateRoom({ ...room, ...payload });
                showAlert({ text: 'Zimmer erfolgreich aktualisiert.' });
            }
            onClose();
        } catch {
            showAlert({
                text: isCreateMode ? 'Fehler beim Anlegen des Zimmers.' : 'Fehler beim Aktualisieren des Zimmers.',
                severity: 'error',
            });
        }
    };

    return (
        <Box sx={{ minWidth: 0 }}>
            <form onSubmit={handleSubmit}>
                {showHeading && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Details
                    </Typography>
                )}
                <TextField
                    name="name"
                    label="Name"
                    value={room.name}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={{ ...fieldSx }}
                />
                <TextField
                    name="description"
                    label="Beschreibung"
                    value={room.description ?? ''}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    multiline
                    minRows={3}
                    sx={{ ...fieldSx }}
                />
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                    <Button onClick={onClose} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        Schließen
                    </Button>
                    <Button type="submit" disabled={isSaving} sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        {isSaving ? 'Speichern...' : 'Speichern'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default RoomEdit;
